import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Mail, Send, Loader2 } from "lucide-react";

const INITIAL = { email: "", subject: "", message: "", website: "" };

export default function ContactSection() {
    const [form, setForm] = useState(INITIAL);
    const [sending, setSending] = useState(false);

    const set = (k) => (e) =>
        setForm((p) => ({ ...p, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.email || !form.subject || !form.message) {
            toast.error("All fields are required");
            return;
        }
        if (!/.+@.+\..+/.test(form.email)) {
            toast.error("Please enter a valid email address");
            return;
        }
        setSending(true);
        try {
            await api.post("/contact", form);
            toast.success("Message sent — we'll get back to you soon.");
            setForm(INITIAL);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Could not send message, please try again.",
            );
        } finally {
            setSending(false);
        }
    };

    return (
        <section
            className="relative z-10 border-t border-white/10 bg-[#0b0b0b]"
            data-testid="contact-section"
            id="contact"
        >
            <div className="px-6 md:px-10 py-16 max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10">
                <div className="md:col-span-5">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF3B30] font-semibold mb-3 inline-flex items-center gap-1.5">
                        <Mail size={11} /> Contact
                    </div>
                    <h2 className="font-anton text-4xl sm:text-5xl uppercase leading-[0.95]">
                        Questions?{" "}
                        <span className="text-[#FF3B30]">Hit us up.</span>
                    </h2>
                    <p className="text-sm text-zinc-400 mt-4 max-w-sm leading-relaxed">
                        Organizer with an event to promote, player looking
                        for a team, or press asking about the commonwealth's
                        lacrosse scene — drop us a line and we'll get back
                        to you within 48 hours.
                    </p>
                    <div className="mt-6 text-[10px] tracking-[0.3em] uppercase text-zinc-600">
                        Unbroken Lacrosse LLC · Virginia
                    </div>
                </div>

                <form
                    onSubmit={submit}
                    className="md:col-span-7 flex flex-col gap-4"
                    data-testid="contact-form"
                    noValidate
                >
                    {/* Honeypot — hidden from humans, bots usually fill it */}
                    <div
                        aria-hidden="true"
                        style={{
                            position: "absolute",
                            left: "-9999px",
                            height: 0,
                            width: 0,
                            overflow: "hidden",
                        }}
                    >
                        <label>
                            Website
                            <input
                                type="text"
                                tabIndex={-1}
                                autoComplete="off"
                                value={form.website}
                                onChange={set("website")}
                            />
                        </label>
                    </div>

                    <div>
                        <label className="text-[10px] tracking-[0.22em] uppercase text-zinc-500 mb-1.5 block">
                            Your Email
                        </label>
                        <input
                            type="email"
                            value={form.email}
                            onChange={set("email")}
                            placeholder="you@example.com"
                            autoComplete="email"
                            className="w-full rounded-none bg-black/40 border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#FF3B30] transition-colors"
                            data-testid="contact-email"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] tracking-[0.22em] uppercase text-zinc-500 mb-1.5 block">
                            Subject
                        </label>
                        <input
                            type="text"
                            value={form.subject}
                            onChange={set("subject")}
                            placeholder="What's this about?"
                            maxLength={200}
                            className="w-full rounded-none bg-black/40 border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#FF3B30] transition-colors"
                            data-testid="contact-subject"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] tracking-[0.22em] uppercase text-zinc-500 mb-1.5 block">
                            Message
                        </label>
                        <textarea
                            value={form.message}
                            onChange={set("message")}
                            placeholder="Tell us more…"
                            rows={5}
                            maxLength={5000}
                            className="w-full rounded-none bg-black/40 border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#FF3B30] transition-colors resize-y"
                            data-testid="contact-message"
                        />
                        <div className="text-right text-[10px] tracking-[0.2em] uppercase text-zinc-600 mt-1">
                            {form.message.length} / 5000
                        </div>
                    </div>
                    <div>
                        <button
                            type="submit"
                            disabled={sending}
                            className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#FF574D] disabled:opacity-50 disabled:cursor-wait text-white font-anton tracking-widest uppercase text-sm px-6 py-3 transition-colors"
                            data-testid="contact-submit"
                        >
                            {sending ? (
                                <>
                                    <Loader2
                                        size={14}
                                        className="animate-spin"
                                    />
                                    Sending
                                </>
                            ) : (
                                <>
                                    <Send size={14} /> Send message
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </section>
    );
}
